import find from 'lodash/find';
import filter from 'lodash/filter';
import remove from 'lodash/remove';
import isEqual from 'lodash/isEqual';

import { PatternText, IVariantText } from './Pattern/Decal/patternText';
import { PatternMask, IVariantMask } from './Pattern/Decal/patternMask';
import {
    PatternDecal, IVariantDecal, DecalType
} from './Pattern/Decal/patternDecal';
import { ContentModelVariantMesh, IVariantMeshes, IVariantMeshesProperties } from './contentModelVariantMesh';

export abstract class ContentModelDecal extends ContentModelVariantMesh {
    protected setAllVariantDecal(
        variantTextList: IVariantText[],
        variantMaskList: IVariantMask[]
    ): void {
        // Remove all Decal first
        this.patternDecals.forEach((patternDecal) => {
            patternDecal.resetVariant();
        });
        // Then apply the new variant Decals
        if (variantTextList) {
            this.setVariantDecalList(variantTextList);
        }
        if (variantMaskList) {
            this.setVariantDecalList(variantMaskList);
        }
    }

    private setVariantDecalList(variantDecalList: IVariantDecal[]) {
        if (variantDecalList) {
            variantDecalList.forEach((variantDecal) => {
                const patternDecal = this.getPatternDecal(variantDecal.meshes);
                if (patternDecal) {
                    patternDecal.setVariant(variantDecal);
                }
            });
        }
    }

    public getVariantDecalList(type?: DecalType): IVariantText[] | IVariantMask[] {
        const variantDecal = [];
        this.patternDecals.forEach((patternDecal) => {
            patternDecal.resetVariant();
            const decalVariant = patternDecal.getVariant();
            const decalGeometry = patternDecal.getGeometry();
            if (type && decalGeometry.type === type) {
                variantDecal.push(decalVariant);
            } else if (!type) {
                variantDecal.push(decalVariant);
            }
        });
        return variantDecal;
    }

    public getVariantTextList(): IVariantText[] {
        return this.getVariantDecalList('text') as IVariantText[];
    }

    public getVariantMaskList(): IVariantMask[] {
        return this.getVariantDecalList('mask') as IVariantMask[];
    }

    private patternDecals: PatternDecal[] = []

    public addDecalVariant(decalVariantMeshes: IVariantMeshesProperties): PatternDecal {
        this.variantMeshesPropertiesList.push(decalVariantMeshes);
        const patternDecal = this.buildPatternDecal(decalVariantMeshes);
        return patternDecal;
    }

    protected buildPatternDecal(decalVariantMeshes: IVariantMeshesProperties): PatternDecal {
        const { decalGeometry, meshes, materials } = decalVariantMeshes;
        let patternDecal: PatternDecal;
        if (decalGeometry.type === 'text') {
            patternDecal = new PatternText(this._system, meshes, decalGeometry);
            if (materials && materials.length) patternDecal.setMaterial(materials[0]);
        } else if (decalGeometry.type === 'mask') {
            patternDecal = new PatternMask(this._system, decalGeometry);
        }
        this.patternDecals.push(patternDecal);
        return patternDecal;
    }

    protected buildPatternDecalWithSourceMesh(
        decalVariantMeshes: IVariantMeshesProperties
    ): PatternDecal {
        const { decalGeometry } = decalVariantMeshes;
        const { sourceMeshName } = decalGeometry;

        const patternDecal = this.buildPatternDecal(decalVariantMeshes);
        const patternMesh = this.getPatternMeshByName(sourceMeshName);
        if (!patternMesh) {
            console.error("Can't find mesh to add Decal variant on it");
            return null;
        }
        const { mesh } = patternMesh;
        patternDecal.setSourceMesh(mesh);
        patternDecal.resetGeometry();
        const variantMesh = this.getVariantMeshByName(sourceMeshName);
        if (variantMesh && !decalGeometry.sourceMeshStayVisible) {
            this.setVariantMeshes(
                { meshes: [decalGeometry.name], material: variantMesh.material },
                false
            );
        } else if (decalVariantMeshes.materials) {
            this.setVariantMeshes(
                { meshes: [decalGeometry.name], material: decalVariantMeshes.materials[0] },
                false
            );
        }

        return patternDecal;
    }

    public updateDecalGeometries(): void {
        this.patternDecals.forEach((patternDecal) => {
            const decalGeometry = patternDecal.getGeometry();
            const vmp = this.getVariantMeshesPropertiesByNakerName(decalGeometry.name);
            vmp.decalGeometry = decalGeometry;
        });
    }

    public showDecals(bool: boolean): void {
        this.patternDecals.forEach((patternDecal) => {
            if (bool) {
                patternDecal.show();
                patternDecal.resetGeometry();
            } else {
                patternDecal.hide();
            }
        });
    }

    public setVariantMeshes(variantMeshes: IVariantMeshes, animate: boolean): void {
        this.removeAnimMesh();
        const { meshes } = variantMeshes;
        const { material } = variantMeshes;
        const firstMeshName = meshes[0];
        const patternText = this.getPatternText(meshes);
        // Means we change a Text material
        if (patternText) {
            const textGeometry = patternText.getGeometry();
            patternText.setVariant({ material });
            this.setVariantMeshesMaterial({ meshes: [firstMeshName], material }, animate);
            // If source mesh follow text material, we have to save new color on source mesh too
            if (!textGeometry.sourceMeshStayVisible) {
                this.setVariantMeshesMaterial({ meshes, material }, animate);
            }
        } else {
            // Else we are on a Classic Material Input
            this.setVariantMeshesMaterial({ meshes, material }, animate);
        }
    }

    public removeDecalVariant(name: string): void {
        const patternDecal = remove(this.patternDecals, (pd) => pd.getGeometry().name === name)[0];
        patternDecal.destroy();
        remove(this.variantMeshesPropertiesList, (pd) => pd.nakerName === name);
        this._unsetVariantMeshes([name]);
    }

    public getPatternDecalNumber(): number {
        return this.patternDecals.length;
    }

    public getPatternDecal(meshes: string[]): PatternDecal {
        return find(this.patternDecals, (p: PatternDecal) => p.meshes === meshes);
    }

    public getPatternText(meshes: string[]): PatternText {
        return find(this.patternDecals, (p: PatternDecal) => isEqual(p.meshes, meshes) && p.getGeometry().type === 'text') as PatternText;
    }

    public getPatternTexts(): PatternText[] {
        return filter(this.patternDecals, (p: PatternDecal) => p.getGeometry().type === 'text') as PatternText[];
    }

    public getPatternMask(meshes: string[]): PatternMask {
        return find(this.patternDecals, (p: PatternDecal) => isEqual(p.meshes, meshes) && p.getGeometry().type === 'mask') as PatternMask;
    }
}
